import { clone, kebabCase, startCase } from "lodash";
import { CROPS, ALIASED_SLUG_LOOKUP } from "./constants";
import { Crop, Crops } from "./interfaces";
import zhMapping from "./data/zh-CN-mapping.json";
import zhCrops from "./data/zh-CN-crops.json";

const GENERIC_PLANT_ICON = "/crops/icons/generic-plant.avif";

const customCrop = (slug: string) => {
  const generic = clone(CROPS["generic-plant"]);
  generic.name = startCase(slug);
  return generic;
};

/**
 * Get crop's display name in Chinese (if available) or English
 */
export const getDisplayName = (slug: string): string => {
  const kebabSlug = kebabCase(slug);
  // Try to get Chinese name from mapping
  const zhName = zhMapping[kebabSlug as keyof typeof zhMapping];
  if (zhName) {
    return zhName;
  }
  // Fallback to English name from crop data
  const crop = findCrop(slug);
  return crop.name;
};

export const findCrop = (slug: string): Crop => {
  const crop = CROPS[kebabCase(slug)];
  if (!crop) {
    return customCrop(slug);
  }

  // Merge Chinese data if available
  const kebabSlug = kebabCase(slug);
  const zhCrop = zhCrops[kebabSlug as keyof typeof zhCrops];
  if (zhCrop) {
    // Create a clone to avoid modifying original data
    const mergedCrop = clone(crop);
    // Only merge specific fields that have Chinese translations
    if (zhCrop.name) mergedCrop.name = zhCrop.name;
    if (zhCrop.description) mergedCrop.description = zhCrop.description;
    if (zhCrop.sun_requirements) mergedCrop.sun_requirements = zhCrop.sun_requirements;
    if (zhCrop.sowing_method) mergedCrop.sowing_method = zhCrop.sowing_method;
    return mergedCrop;
  }

  return crop;
};

export const findCrops = (searchTerm: string): Crops => {
  // If search term is empty or only whitespace, return all crops
  if (!searchTerm || searchTerm.trim() === "") {
    return getAllCrops();
  }

  const term = searchTerm.toLowerCase();
  const crops = Object.entries(CROPS).filter(([slug, crop]) => {
    const kebabSlug = kebabCase(slug);

    // Check English name
    if (crop.name.toLowerCase().includes(term)) {
      return true;
    }

    // Check common names
    if (crop.common_names.some(name =>
      name.toLowerCase().includes(term))) {
      return true;
    }

    // Check Chinese name
    const zhName = zhMapping[kebabSlug as keyof typeof zhMapping];
    if (zhName && zhName.toLowerCase().includes(term)) {
      return true;
    }

    return false;
  })
  .reduce((crops, [slug]) => {
    // Use findCrop to get merged Chinese data
    crops[kebabCase(slug)] = findCrop(slug);
    return crops;
  }, {} as Crops);

  if (Object.values(crops).length == 0) {
    return { [kebabCase(searchTerm)]: customCrop(searchTerm) };
  }
  return crops;
};

/**
 * Get all crops with merged Chinese data
 */
export const getAllCrops = (): Crops => {
  return Object.entries(CROPS).reduce((crops, [slug]) => {
    crops[kebabCase(slug)] = findCrop(slug);
    return crops;
  }, {} as Crops);
};

export const findIcon = (slug: string): string => {
  let icon = findCrop(slug).icon;
  if (icon == GENERIC_PLANT_ICON) {
    icon = findCrop(ALIASED_SLUG_LOOKUP[slug]).icon;
  }
  return icon || GENERIC_PLANT_ICON;
};

export const findImage = (slug: string): string => {
  return findCrop(slug).image;
};
